import type {
	IDesign,
	ITrack,
	ITrackItem,
} from "@designcombo/types";
import type {
	CompositionVideoShortsPropsType,
	Short,
	ShortWithRelations,
	VideoWithRelations,
} from "@repo/types";
import { DefaultWaterMarkOptions } from "@repo/types";
import type { AsdConfigWithRelations, WaterMarkSchemaType } from "@repo/types/myTypes";
import { generateId } from "../trackId";
import {
	createCaptionLayerPayloads,
	getWindowedFrameSubs,
	splitSentences,
} from "./caption-mapper";
import { mapShortToVideoTrack } from "./video-mapper";
import { createWatermarkLayerPayload, removeDefaultWatermarks } from "./watermark-mapper";
import { createBRollLayersFromShort } from "./broll-mapper";

export {
	createCaptionLayerPayloads,
	getWindowedFrameSubs,
	splitSentences,
} from "./caption-mapper";
export type {
	ClipOptionsStylePhaseSchemaType,
	ClipOptionsStyleSchemaType,
	ClipPhaseAnimationOptionsType,
	ClipOptionsStyleProperty,
	SubtitleSentence,
} from "./caption-mapper";
export { SILENCE_THRESHOLD_MS } from "./caption-mapper";
export {
	computeUniformContainLayout,
	computeUniformCoverLayout,
	createImageTrackItem,
	getImageDimensionsForWidth,
	getScaleForTargetSize,
} from "./image-mapper";
export type { UniformContainLayout, UniformCoverLayout } from "./image-mapper";
export {
	createWatermarkLayerPayload, 
	isDefaultWatermarkUrl,
	removeDefaultWatermarks,
} from "./watermark-mapper";
export { mapShortToVideoTrack, createVideoDetails } from "./video-mapper";
export * from "./regroup-captions";
export {
	BROLL_TRACK_NAME_PREFIX,
	createBRollLayersFromShort,
} from "./broll-mapper";
export {
	mergeAdjacentBRollClipTimings,
	type BRollClipTiming,
} from "./broll-clip-timing-merge";

export interface ITrackData {
	trackItems: ITrackItem[];
	tracks: ITrack[];
}

// Function to regenerate captions with new maxWordsInPage from original video data
export function regenerateCaptionsWithMaxWords(
	video: VideoWithRelations,
	targetShort: Short,
	contentOptions: any,
	maxWordsInPage: number,
	resolution: {
		width: number,
		height: number
	},
	fps = 30,
): ITrackItem[] {
	// Step 1: Get windowed frame subtitles (same as original)
	const windowedFrameSubs = getWindowedFrameSubs(
		video,
		targetShort.startTime,
		targetShort.endTime,
		contentOptions,
		fps,
	);

	// Step 2: Split into sentences with new maxWordsInPage
	const splitedSentences = splitSentences(windowedFrameSubs, maxWordsInPage);

	// Step 3: Create caption layer payloads (same as original)
	const captionLayers = createCaptionLayerPayloads(
		splitedSentences,
		targetShort,
		fps,
		resolution,
	);

	return captionLayers;
}

// Mapper function to convert video shorts data to a design with only a video layer
export function VideoShortsToVideoLayer(
	videoData: CompositionVideoShortsPropsType,
	fps = 30,
	forcedSrc = "",
	addAsd = false,
): IDesign {
	const video = videoData.video as unknown as VideoWithRelations;
	const targetShort = video.shorts?.find(
		(short) => short.id === videoData.target,
	) as ShortWithRelations;
	const director = videoData.options?.director as AsdConfigWithRelations;

	if (!targetShort) {
		return {
			id: generateId(10),
			fps,
			tracks: [],
			size: {
				width: videoData?.resolution?.width || 1080,
				height: videoData?.resolution?.height || 1920,
			},
			duration: 0,
			trackItemIds: [],
			transitionsMap: {},
			trackItemsMap: {},
			transitionIds: [],
		};
	}

	const trackItems: ITrackItem[] = [];
	const tracks: ITrack[] = [];

	const { trackItem: videoTrackItem, track: videoTrack, duration: videoDuration } =
		mapShortToVideoTrack({
			videoData,
			targetShort,
			video,
			director,
			includeAsd: addAsd,
			forcedSrc: forcedSrc || undefined,
			overrides: {
				volume: 1,
			},
		});

	trackItems.push(videoTrackItem);
	tracks.push(videoTrack);

	const trackItemsMap: Record<string, ITrackItem> = {
		[videoTrackItem.id]: videoTrackItem,
	};
	const trackItemIds: string[] = [videoTrackItem.id];

	return {
		id: generateId(10),
		fps: fps,
		tracks: tracks,
		size: {
			width: videoData.resolution.width,
			height: videoData.resolution.height,
		},
		duration: videoDuration,
		trackItemIds: trackItemIds,
		transitionsMap: {},
		trackItemsMap: trackItemsMap,
		transitionIds: [],
	};
}

// Mapper function to convert video shorts data to caption layers only
export async function VideoShortsToCaptionLayers(
	videoData: CompositionVideoShortsPropsType,
	_generatePreview = true,
	fps = 30,
	withCaptions = true,
): Promise<IDesign> {
	console.log("Mapping video shorts data to caption layers:", videoData);
	const video = videoData.video as unknown as VideoWithRelations;
	const targetShort = video.shorts?.find(
		(short) => short.id === videoData.target,
	) as ShortWithRelations;
	const director = videoData.options?.director as AsdConfigWithRelations;
	const maxWordsInPage = videoData?.options?.maxWordsInPage || 6;

	if (!targetShort) {
		return {
			id: generateId(10),
			fps,
			tracks: [],
			size: {
				width: videoData?.resolution?.width || 1080,
				height: videoData?.resolution?.height || 1920,
			},
			duration: 0,
			trackItemIds: [],
			transitionsMap: {},
			trackItemsMap: {},
			transitionIds: [],
		};
	}

	const trackItems: ITrackItem[] = [];
	const tracks: ITrack[] = [];

	const waterMarkVideoDuration = (targetShort.endTime - targetShort.startTime) * 1000;
	// Create watermark layer if provided
	console.log("Watermark options (Caption):", videoData.options.waterMark);
	if (videoData?.options?.waterMark?.waterMarkUrl) {
		const watermarkLayer = await createWatermarkLayerPayload(
			waterMarkVideoDuration,
			videoData.resolution,
			videoData.options.waterMark as WaterMarkSchemaType,
		);
		if (watermarkLayer) {
			trackItems.push(watermarkLayer);
			console.log(
				"Watermark layer added to trackItems (Caption):",
				watermarkLayer.id,
			);
		} 
		
		// Create watermark track if watermark exists
		let watermarkTrack: ITrack | null = null;
		if (watermarkLayer) {
			watermarkTrack = {
				id: generateId(10),
				type: "image",
				items: [watermarkLayer.id],
				accepts: ["image"],
				maxWordsInPage
				// styles: ,
			};
		}

		if (watermarkTrack) {
			tracks.push(watermarkTrack);
		}
	} else {
		console.log(
			"No watermark layer created (Caption) - missing waterMarkUrl or invalid options",
		);
	}

	// First, create video layer payload
	const isCropped = Boolean(targetShort.cropFileUrl);
	const { trackItem: videoTrackItem, track: videoTrack, duration: videoDuration } =
		mapShortToVideoTrack({
			videoData,
			targetShort,
			video,
			director,
			includeAsd: true,
			trim: {
				from: !isCropped ? targetShort.startTime * 1000 : 0,
				to: !isCropped
					? targetShort.endTime * 1000
					: (targetShort.endTime - targetShort.startTime) * 1000,
			},
		});

	trackItems.push(videoTrackItem);

	const { trackItems: bRollTrackItems, tracks: bRollTracks } =
		await createBRollLayersFromShort(targetShort, videoData.resolution);
	if (bRollTrackItems.length > 0) {
		trackItems.push(...bRollTrackItems);
	}

	console.log("Created video layer payload:", {
		id: videoTrackItem.id,
		src: targetShort.cropFileUrl,
		duration: videoDuration,
		startTime: targetShort.startTime,
		endTime: targetShort.endTime,
	});

	let captionTrack: ITrack | null = null;
	if (withCaptions) {
		

		// Get windowed frame subtitles (same as useWindowedFrameSubs)
		const windowedFrameSubs = getWindowedFrameSubs(
			video,
			targetShort.startTime,
			targetShort.endTime,
			videoData.options?.contentOptions || {},
			fps,
		);

		// Split into sentences (same as splitSentences)
		const splitedSentences = splitSentences(windowedFrameSubs, maxWordsInPage);

		console.log("Windowed frame subs:", windowedFrameSubs);
		console.log("Split sentences:", splitedSentences);
		console.log("Max words per page:", maxWordsInPage);
		// Then create caption layer payloads for each sentence
		const captionLayers = createCaptionLayerPayloads(
			splitedSentences,
			targetShort,
			fps,
			videoData.resolution,
		);
		trackItems.push(...captionLayers);
		captionTrack = {
			id: generateId(10),
			type: "caption",
			items: captionLayers.map((item) => item.id),
			accepts: ["caption"],
			maxWordsInPage: maxWordsInPage,
			styles: videoData.options?.style,
			...(videoData.options?.wordEffects
				? { wordEffects: videoData.options.wordEffects }
				: {}),
		};
	}

	if (bRollTracks.length > 0) {
		tracks.push(...bRollTracks);
	}

	if (captionTrack) {
		tracks.push(captionTrack);
	}

	videoTrack.items = [videoTrackItem.id];
	tracks.push(videoTrack);

	// Convert trackItems to trackItemsMap
	const trackItemsMap: Record<string, ITrackItem> = {};
	const trackItemIds: string[] = [];

	trackItems.forEach((item) => {
		trackItemsMap[item.id] = item;
		trackItemIds.push(item.id);
	});

	// Calculate total duration
	const totalDuration = Math.max(...trackItems.map((item) => item.duration || 0), 0);

	// Return the processed data as IDesign
	return {
		id: generateId(10),
		fps: fps,
		tracks: tracks,
		size: {
			width: videoData.resolution.width,
			height: videoData.resolution.height,
		},
		duration: totalDuration,
		trackItemIds: trackItemIds,
		transitionsMap: {},
		trackItemsMap: trackItemsMap,
		transitionIds: [],
	};
}

// Mapper function to convert video shorts data to editor layers
export async function VideoShortsToEditorLayers(
	videoData: CompositionVideoShortsPropsType,
	_generatePreview = true,
	fps = 30,
	withCaptions = true,
): Promise<IDesign> {
	// Use captions by default instead of text
	// Note: Captions are automatically limited to the main video duration
	return VideoShortsToCaptionLayers(videoData, _generatePreview, fps, withCaptions);
}

// Function to process design objects and add watermark for free users or remove default watermarks for paid users
export async function processDesignPayload(
	design: IDesign,
	_resolution: { width: number; height: number },
	isFreeUser: boolean,
): Promise<IDesign> {
	// If user is not free, remove any existing default watermarks
	const cleanedDesign = removeDefaultWatermarks(design);
	if (!isFreeUser) {
		return cleanedDesign;
	}

	// Create a copy of the design to avoid mutating the original
	const processedDesign = { ...cleanedDesign };
	
	// Ensure required properties exist
	if (!processedDesign.trackItemsMap) {
		processedDesign.trackItemsMap = {};
	}
	if (!processedDesign.trackItemIds) {
		processedDesign.trackItemIds = [];
	}
	if (!processedDesign.tracks) {
		processedDesign.tracks = [];
	}

	// Calculate duration from trackItems - find the maximum end time
	let maxEndTime = 0;
	Object.values(processedDesign.trackItemsMap).forEach((item) => {
		if (item.display?.to && item.display.to > maxEndTime) {
			maxEndTime = item.display.to;
		}
	});

	// Override duration to match the end of trackItems
	const calculatedDuration = maxEndTime || processedDesign.duration || 30000;
	processedDesign.duration = calculatedDuration;

	// Find the maximum zIndex in the design
	let maxZIndex = 0;
	Object.values(processedDesign.trackItemsMap).forEach((item) => {
		if (item.details?.zIndex && item.details.zIndex > maxZIndex) {
			maxZIndex = item.details.zIndex;
		}
	});

	const watermarkLayer = await createWatermarkLayerPayload(
		calculatedDuration,
		_resolution,
		DefaultWaterMarkOptions,
	);

	// Add watermark to trackItemsMap
	processedDesign.trackItemsMap[watermarkLayer.id] = watermarkLayer;
	processedDesign.trackItemIds.push(watermarkLayer.id);

	// Create or update watermark track
	let watermarkTrack = processedDesign.tracks.find(track => track.type === "image");
	if (!watermarkTrack) {
		watermarkTrack = {
			id: generateId(10),
			type: "image",
			items: [watermarkLayer.id],
			accepts: ["image"],
		};
		processedDesign.tracks.push(watermarkTrack);
	} else {
		watermarkTrack.items.push(watermarkLayer.id);
	}

	console.log("Added watermark to design for free user:", watermarkLayer.id);
	
	return processedDesign;
}
